import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    HostBinding,
    Input,
    NgModule,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChildren,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import {CommonModule} from "@angular/common";
import {AzoraSlot} from "@azora/core";
import {Subject} from "rxjs";
import {LabelPosition} from "../control-group/option";
import {coerceBooleanProperty} from "@angular/cdk/coercion";
import {DomSanitizer, SafeStyle} from "@angular/platform-browser";
import _ from 'lodash'

@Component({
    selector: 'az-switch',
    templateUrl: './switch.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AzoraSwitch implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
    
    @Input() label = ''
    @Input() value = '';
    @Input() name = ''
    
    @Output()
    change = new Subject();
    
    @Output()
    modelChange = new Subject()
    
    @Output()
    blur = new Subject();
    @Output()
    focus = new Subject();
    
    /** Whether the label should appear after or before the Switch button. Defaults to 'after' */
    @Input()
    @HostBinding('attr.labelPosition')
    get labelPosition(): LabelPosition {
        return this._labelPosition
    }
    
    set labelPosition(newLabelPos: LabelPosition) {
        if (newLabelPos && newLabelPos !== this._labelPosition) {
            this._labelPosition = newLabelPos;
        }
    }
    
    /** Whether the Switch button is disabled. */
    @Input()
    @HostBinding('attr.disabled')
    get disabled(): boolean {
        return this._disabled
    }
    
    set disabled(value: boolean) {
        const newDisabledState = coerceBooleanProperty(value);
        if (this._disabled !== newDisabledState) {
            this._disabled = newDisabledState;
            this._cd.markForCheck();
        }
    }
    
    /** Whether this Switch button is checked. */
    @Input()
    @HostBinding('attr.checked')
    get checked(): boolean {
        return this._checked;
    }
    
    set checked(value: boolean) {
        const newCheckedState = coerceBooleanProperty(value);
        if (this._checked !== newCheckedState) {
            this._checked = newCheckedState;
            
            this._cd.markForCheck();
            
        }
    }
    
    
    @Input()
    get model() {
        return this._model
    }
    
    set model(newModel: any) {
        if (newModel !== this._model) {
            this._model = newModel
            // this.checked = _.isArray(newModel)
            //         ? !!<any[]>newModel.includes(this.value)
            //         : this.value === newModel
        }
    }
    
    /** Theme color palette for the component. */
    @Input()
    @HostBinding('style.color')
    get color(): string | SafeStyle {
        return this.checked ? this._color : this._sanitizer.bypassSecurityTrustStyle('#c5c6c7')
    };
    
    set color(newColor: string | SafeStyle) {
        if (newColor && newColor !== this._color) {
            this._color = <string>newColor
        }
    }
    
    private _color = "#027be3"
    
    
    private get _inputElement(): HTMLInputElement {
        return this._element.nativeElement.querySelector('input');
    }
    
    /** Whether this Switch is checked. */
    private _checked = false;
    
    private _labelPosition: LabelPosition = 'right';
    
    /** Whether this Switch is disabled. */
    private _disabled: boolean;
    
    private _model = null
    
    /** Whether this Switch is required. */
    private _required: boolean;
    
    /** Value assigned to this Switch. */
    private _value: any = null;
    
    /**
     *  Template for AzSwitch content.
     * @docs-private
     */
    @ViewChildren(AzoraSlot)
    private _implicitContent: QueryList<AzoraSlot>;
    
    /**
     * Template provided inside the AzSwitch view that will be used if present
     * @docs-private
     */
    @ContentChildren(AzoraSlot)
    private _explicitContent: QueryList<AzoraSlot>;
    
    
    constructor(
            private _vcRef: ViewContainerRef,
            private _cd: ChangeDetectorRef,
            private _element: ElementRef,
            private _renderer: Renderer2,
            private _sanitizer: DomSanitizer
    ) {
    }
    
    ngOnInit() {
    }
    
    ngOnChanges(changes: SimpleChanges): void {
    }
    
    ngAfterViewChecked(): void {
    
    }
    
    ngOnDestroy(): void {
    }
    
    // onChange = (e: Event) => {
    //     e.preventDefault()
    //     e.stopPropagation()
    //
    //     this.modelChange.next(this.value);
    //     this.change.next(this.value)
    // };
    
    onFocus = (e: Event) => {
        e.stopPropagation();
        
        // this.focus.emit();
    };
    
    onBlur = (e: Event) => {
        e.stopPropagation();
        // this.blur.emit();
    };
    
    _onInputClick(event: Event) {
        // We have to stop propagation for click events on the visual hidden input element.
        // By default, when a user clicks on a label element, a generated click event will be
        // dispatched on the associated input element. Since we are using a label element as our
        // root container, the click event on the `Switch-button` will be executed twice.
        // The real click event will bubble up, and the generated click event also tries to bubble up.
        // This will lead to multiple click events.
        // Preventing bubbling for the second event will solve that issue.
        event.stopPropagation();
    }
    
    /**
     * Triggered when the Switch button received a click or the input recognized any change.
     * Clicking on a label element, will trigger a change event on the associated input.
     */
    _onInputChange(event: Event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
        
        this.checked = !this.checked;
        this._emitChangeEvent();
    }
    
    /** Dispatch change event with current value. */
    private _emitChangeEvent(): void {
        let emitValue
        if (this.checked) {
            emitValue = _.isArray(this.model) ? this.model.concat(this.value) : [this.model, this.value]
        } else {
            emitValue = _.without(this.model, this.value)
        }
        
        emitValue = emitValue.length > 1 ? emitValue : emitValue[0] || null
        
        console.log(emitValue)
        this.modelChange.next(emitValue);
        this.change.next(emitValue)
    }
}

const COMPONENT = [AzoraSwitch]

@NgModule({
    imports: [
        CommonModule
    ],
    declarations: COMPONENT,
    exports: COMPONENT,
    entryComponents: COMPONENT
})
export class AzoraSwitchModule {
}
